"use client";

import { useId } from "react";
import {
  BARRIER_STYLE,
  ELEMENT_COLORS,
  H,
  ICON_META,
  LEGEND_ROW,
  LEGEND_WIDTH,
  ROUTE_STYLE,
  W,
  ZONE_STYLE,
  findCalibration,
  formatMetres,
  legendEntries,
  metresPerUnit,
  pointsAlongPolyline,
  polylineMidpoint,
  type BarrierEl,
  type BarrierType,
  type IconType,
  type PlanBackground,
  type PlanElement,
  type RouteType,
  type ShapeType,
  type ZoneType,
} from "@/components/tools/siteplan/model";

/* ── Shared SVG renderer (editor + print) ────────────────────────────────── */

/** Arrow-head marker id for an arbitrary stroke colour (one marker per palette colour). */
function colorMarkerId(markerPrefix: string, color: string): string {
  const idx = (ELEMENT_COLORS as readonly string[]).indexOf(color);
  return idx >= 0 ? `${markerPrefix}-arrow-c${idx}` : `${markerPrefix}-arrow-c0`;
}

function ShapeBody({
  shape,
  x,
  y,
  w,
  h,
  ...rest
}: {
  shape: ShapeType;
  x: number;
  y: number;
  w: number;
  h: number;
} & React.SVGAttributes<SVGElement>) {
  if (shape === "ellipse") return <ellipse cx={x + w / 2} cy={y + h / 2} rx={w / 2} ry={h / 2} {...rest} />;
  if (shape === "triangle") return <polygon points={`${x + w / 2},${y} ${x + w},${y + h} ${x},${y + h}`} {...rest} />;
  return <rect x={x} y={y} width={w} height={h} {...rest} />;
}

/** ISO 7010-style marker badge: warning triangle, safe/fire square, or circle. */
export function IconBadge({ icon, x, y, size = 1, color, selected }: { icon: IconType; x: number; y: number; size?: number; color?: string; selected?: boolean }) {
  const m = ICON_META[icon];
  const r = 13 * size;
  const shape = m.shape ?? "circle";
  const fg = m.fg ?? "white";
  const bg = color ?? m.bg;
  const fs = (m.glyph.length > 1 ? 10.5 : 13) * size * (shape === "triangle" ? 0.85 : 1);
  return (
    <g>
      {selected && <circle cx={x} cy={y} r={r + 6} fill="none" stroke="#0f172a" strokeDasharray="3 3" />}
      {shape === "circle" && <circle cx={x} cy={y} r={r} fill={bg} stroke={m.ring ?? "white"} strokeWidth={(m.ring ? 2.6 : 2) * size} />}
      {shape === "square" && (
        <rect x={x - r} y={y - r} width={r * 2} height={r * 2} rx={3 * size} fill={bg} stroke="white" strokeWidth={2 * size} />
      )}
      {shape === "triangle" && (
        <polygon
          points={`${x},${y - r * 1.3} ${x + r * 1.25},${y + r * 0.95} ${x - r * 1.25},${y + r * 0.95}`}
          fill={bg}
          stroke="#111827"
          strokeWidth={1.4 * size}
          strokeLinejoin="round"
        />
      )}
      <text
        x={x}
        y={y + fs * 0.35 + (shape === "triangle" ? r * 0.22 : 0)}
        fontSize={fs}
        fontWeight="800"
        fill={fg}
        textAnchor="middle"
      >
        {m.glyph}
      </text>
      {icon === "north" && (
        <text x={x} y={y - r - 5} fontSize={11 * size} fontWeight="700" fill="#475569" textAnchor="middle">
          ↑
        </text>
      )}
    </g>
  );
}

/** HTML marker swatch for toolbars, palettes and legends — mirrors IconBadge. */
export function IconSwatch({ icon, className = "h-5 w-5 text-[0.6rem]" }: { icon: IconType; className?: string }) {
  const m = ICON_META[icon];
  const shape = m.shape ?? "circle";
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 items-center justify-center font-extrabold ${className} ${
        shape === "circle" ? "rounded-full" : shape === "square" ? "rounded-[4px]" : ""
      }`}
      style={{
        background: m.bg,
        color: m.fg ?? "white",
        boxShadow: m.ring ? `inset 0 0 0 2px ${m.ring}` : undefined,
        clipPath: shape === "triangle" ? "polygon(50% 0, 100% 100%, 0 100%)" : undefined,
        paddingTop: shape === "triangle" ? "0.35em" : undefined,
        fontSize: shape === "triangle" ? "0.85em" : undefined,
      }}
    >
      {m.glyph}
    </span>
  );
}

/** Linear feature renderer: boundary posts, barrier ticks, walkway corridor, lettered service runs. */
function BarrierLine({ el, selected }: { el: BarrierEl; selected?: boolean }) {
  const s = BARRIER_STYLE[el.barrierType];
  const pts = el.points.map((p) => `${p.x},${p.y}`).join(" ");
  const width = selected ? s.width + 1.5 : s.width;
  return (
    <g>
      {s.deco === "double" ? (
        <>
          <polyline points={pts} fill="none" stroke={s.stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" />
          <polyline points={pts} fill="none" stroke="white" strokeWidth={Math.max(2, width - 3.5)} strokeLinecap="round" strokeLinejoin="round" />
          <polyline points={pts} fill="none" stroke="#94a3b8" strokeWidth={1.2} strokeDasharray="6 6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      ) : (
        <polyline
          points={pts}
          fill="none"
          stroke={s.stroke}
          strokeWidth={width}
          strokeDasharray={s.dash}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {s.deco === "posts" &&
        pointsAlongPolyline(el.points, 26).map((p, i) => (
          <line
            key={i}
            x1={p.x}
            y1={p.y - 4.5}
            x2={p.x}
            y2={p.y + 4.5}
            stroke={s.stroke}
            strokeWidth={2}
            transform={`rotate(${p.angle + 90} ${p.x} ${p.y})`}
          />
        ))}
      {s.deco === "ticks" &&
        pointsAlongPolyline(el.points, 16).map((p, i) => (
          <line
            key={i}
            x1={p.x}
            y1={p.y}
            x2={p.x}
            y2={p.y - 5}
            stroke={s.stroke}
            strokeWidth={1.8}
            transform={`rotate(${p.angle + 90} ${p.x} ${p.y})`}
          />
        ))}
      {s.deco === "letter" &&
        el.points.length >= 2 &&
        (() => {
          const mid = polylineMidpoint(el.points);
          return (
            <g>
              <circle cx={mid.x} cy={mid.y} r={7.5} fill="white" stroke={s.stroke} strokeWidth={1.6} />
              <text x={mid.x} y={mid.y + 3.5} fontSize="9.5" fontWeight="800" fill={s.stroke} textAnchor="middle">
                {s.letter}
              </text>
            </g>
          );
        })()}
    </g>
  );
}

/** Auto-built legend rendered inside the SVG so it prints with the plan. */
function CanvasLegend({ el, elements, selected }: { el: { x: number; y: number }; elements: PlanElement[]; selected?: boolean }) {
  const entries = legendEntries(elements);
  const height = 26 + entries.length * LEGEND_ROW;
  return (
    <g>
      <rect
        x={el.x}
        y={el.y}
        width={LEGEND_WIDTH}
        height={height}
        rx={6}
        fill="rgba(255,255,255,0.92)"
        stroke={selected ? "#0f172a" : "#cbd5e1"}
        strokeWidth={selected ? 1.6 : 1}
        strokeDasharray={selected ? "4 3" : undefined}
      />
      <text x={el.x + 9} y={el.y + 16} fontSize="10.5" fontWeight="800" fill="#475569" letterSpacing="0.06em">
        KEY
      </text>
      {entries.map((entry, i) => {
        const y = el.y + 24 + i * LEGEND_ROW;
        return (
          <g key={`${entry.swatchKind}-${entry.key}`}>
            {entry.swatchKind === "zone" && (
              <rect x={el.x + 9} y={y} width={15} height={9} fill={ZONE_STYLE[entry.key as ZoneType].fill} stroke={ZONE_STYLE[entry.key as ZoneType].stroke} strokeWidth="1.4" />
            )}
            {entry.swatchKind === "route" && (
              <line
                x1={el.x + 9}
                y1={y + 4.5}
                x2={el.x + 24}
                y2={y + 4.5}
                stroke={ROUTE_STYLE[entry.key as RouteType].stroke}
                strokeWidth="2.4"
                strokeDasharray={ROUTE_STYLE[entry.key as RouteType].dash}
              />
            )}
            {entry.swatchKind === "barrier" && (
              <line
                x1={el.x + 9}
                y1={y + 4.5}
                x2={el.x + 24}
                y2={y + 4.5}
                stroke={BARRIER_STYLE[entry.key as BarrierType].stroke}
                strokeWidth={Math.min(4, BARRIER_STYLE[entry.key as BarrierType].width)}
                strokeDasharray={BARRIER_STYLE[entry.key as BarrierType].dash ?? (BARRIER_STYLE[entry.key as BarrierType].deco === "posts" ? undefined : "5 3")}
              />
            )}
            {entry.swatchKind === "icon" && <IconBadge icon={entry.key as IconType} x={el.x + 16.5} y={y + 4.5} size={0.5} />}
            <text x={el.x + 30} y={y + 8} fontSize="9.5" fill="#0f172a">
              {entry.label}
            </text>
          </g>
        );
      })}
      {entries.length === 0 && (
        <text x={el.x + 9} y={el.y + 22 + LEGEND_ROW} fontSize="9.5" fill="#64748b">
          Add elements to build the key
        </text>
      )}
    </g>
  );
}

/** Scale bar shown bottom-left once a calibration reference exists. */
function ScaleBar({ elements }: { elements: PlanElement[] }) {
  const mpu = metresPerUnit(elements);
  if (mpu === null) return null;
  // Pick a round metre figure that renders 60–150 canvas units wide.
  const targets = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500];
  let metres = targets[targets.length - 1];
  for (const t of targets) {
    if (t / mpu >= 60) {
      metres = t;
      break;
    }
  }
  const len = Math.min(metres / mpu, 220);
  const x = 14;
  const y = H - 18;
  return (
    <g>
      <rect x={x - 6} y={y - 14} width={len + 12 + 34} height={24} rx={4} fill="rgba(255,255,255,0.85)" />
      <line x1={x} y1={y} x2={x + len} y2={y} stroke="#0f172a" strokeWidth="2" />
      <line x1={x} y1={y - 5} x2={x} y2={y + 5} stroke="#0f172a" strokeWidth="2" />
      <line x1={x + len} y1={y - 5} x2={x + len} y2={y + 5} stroke="#0f172a" strokeWidth="2" />
      <text x={x + len + 7} y={y + 4} fontSize="11" fontWeight="700" fill="#0f172a">
        {metres} m
      </text>
    </g>
  );
}

export function PlanSvg({
  elements,
  background,
  selectedId,
  draftRect,
  draftRoute,
  draftRouteType,
  draftBarrier,
  draftBarrierType,
  draftLine,
  draftDraw,
  markerPrefix,
  children,
}: {
  elements: PlanElement[];
  background?: PlanBackground | null;
  selectedId?: number | null;
  draftRect?: { x: number; y: number; w: number; h: number; zoneType?: ZoneType; shape?: ShapeType; color?: string } | null;
  draftRoute?: { x: number; y: number }[] | null;
  draftRouteType?: RouteType;
  draftBarrier?: { x: number; y: number }[] | null;
  draftBarrierType?: BarrierType;
  draftLine?: { x1: number; y1: number; x2: number; y2: number; color: string; dash?: boolean } | null;
  draftDraw?: { points: { x: number; y: number }[]; color: string; width: number; highlight?: boolean } | null;
  markerPrefix: string;
  children?: React.ReactNode;
}) {
  const mpu = metresPerUnit(elements);
  return (
    <>
      <defs>
        {(Object.keys(ROUTE_STYLE) as RouteType[]).map((rt) => (
          <marker
            key={rt}
            id={`${markerPrefix}-arrow-${rt}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={ROUTE_STYLE[rt].stroke} />
          </marker>
        ))}
        {/* One arrow head per palette colour — lines/arrows look theirs up by colour. */}
        {ELEMENT_COLORS.map((c, i) => (
          <marker
            key={c}
            id={`${markerPrefix}-arrow-c${i}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6.5"
            markerHeight="6.5"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
          </marker>
        ))}
        <pattern id={`${markerPrefix}-hatch`} patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="rgba(220,38,38,0.35)" strokeWidth="2" />
        </pattern>
        <pattern id={`${markerPrefix}-grid`} patternUnits="userSpaceOnUse" width="40" height="40">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="1" />
        </pattern>
      </defs>

      <rect x="0" y="0" width={W} height={H} fill="white" />
      {background?.dataUrl && (
        <image
          href={background.dataUrl}
          x="0"
          y="0"
          width={W}
          height={H}
          preserveAspectRatio="xMidYMid meet"
          opacity={Math.max(0.12, Math.min(0.85, background.opacity))}
        />
      )}
      <rect x="0" y="0" width={W} height={H} fill={`url(#${markerPrefix}-grid)`} opacity={background?.dataUrl ? 0.58 : 1} />
      <rect x="0.5" y="0.5" width={W - 1} height={H - 1} fill="none" stroke="#cbd5e1" />

      {/* Inserted images (above background, below all marks) */}
      {elements.map((el) =>
        el.kind === "image" ? (
          <g key={el.id} transform={el.rotation ? `rotate(${el.rotation} ${el.x + el.w / 2} ${el.y + el.h / 2})` : undefined}>
            <image href={el.dataUrl} x={el.x} y={el.y} width={el.w} height={el.h} preserveAspectRatio="none" opacity={el.opacity ?? 1} />
            <rect
              x={el.x}
              y={el.y}
              width={el.w}
              height={el.h}
              fill="none"
              stroke={el.id === selectedId ? "#0f172a" : "rgba(15,23,42,0.18)"}
              strokeWidth={el.id === selectedId ? 1.6 : 1}
              strokeDasharray={el.id === selectedId ? "4 3" : undefined}
            />
          </g>
        ) : null,
      )}

      {/* Zones (bottom mark layer) */}
      {elements.map((el) => {
        if (el.kind !== "zone") return null;
        const s = ZONE_STYLE[el.zoneType];
        const stroke = el.color ?? s.stroke;
        const dims = el.id === selectedId && mpu !== null ? `${formatMetres(el.w, mpu)} × ${formatMetres(el.h, mpu)}` : null;
        return (
          <g key={el.id} transform={el.rotation ? `rotate(${el.rotation} ${el.x + el.w / 2} ${el.y + el.h / 2})` : undefined}>
            <rect
              x={el.x}
              y={el.y}
              width={el.w}
              height={el.h}
              fill={el.zoneType === "exclusion" && !el.color ? `url(#${markerPrefix}-hatch)` : el.color ? `${el.color}26` : s.fill}
              stroke={stroke}
              strokeWidth={el.id === selectedId ? 3 : 2}
              strokeDasharray={el.zoneType === "exclusion" ? "6 4" : undefined}
            />
            {el.label && (
              <text x={el.x + 6} y={el.y + 16} fontSize="12" fontWeight="700" fill={stroke}>
                {el.label}
              </text>
            )}
            {dims && (
              <text x={el.x + 6} y={el.y + el.h - 7} fontSize="10.5" fontWeight="600" fill={stroke}>
                {dims}
              </text>
            )}
          </g>
        );
      })}

      {/* Crane radii under routes */}
      {elements.map((el) =>
        el.kind === "icon" && el.icon === "crane" && el.radius ? (
          <g key={`r-${el.id}`}>
            <circle cx={el.x} cy={el.y} r={el.radius} fill="rgba(220,38,38,0.06)" stroke="#dc2626" strokeWidth="1.5" strokeDasharray="6 5" />
            {mpu !== null && (
              <text x={el.x} y={el.y - el.radius - 4} fontSize="10.5" fontWeight="700" fill="#dc2626" textAnchor="middle">
                R {formatMetres(el.radius, mpu)}
              </text>
            )}
          </g>
        ) : null,
      )}

      {/* Annotation shapes */}
      {elements.map((el) =>
        el.kind === "shape" ? (
          <g key={el.id} transform={el.rotation ? `rotate(${el.rotation} ${el.x + el.w / 2} ${el.y + el.h / 2})` : undefined}>
            <ShapeBody
              shape={el.shape}
              x={el.x}
              y={el.y}
              w={el.w}
              h={el.h}
              fill={el.filled ? `${el.color}26` : "none"}
              stroke={el.color}
              strokeWidth={el.id === selectedId ? 3 : 2}
            />
          </g>
        ) : null,
      )}

      {/* Barriers, boundaries & service runs */}
      {elements.map((el) =>
        el.kind === "barrier" ? <BarrierLine key={el.id} el={el} selected={el.id === selectedId} /> : null,
      )}

      {/* Routes */}
      {elements.map((el) => {
        if (el.kind !== "route") return null;
        const s = ROUTE_STYLE[el.routeType];
        return (
          <polyline
            key={el.id}
            points={el.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke={el.color ?? s.stroke}
            strokeWidth={el.id === selectedId ? 5 : 3.5}
            strokeDasharray={s.dash}
            strokeLinecap="round"
            strokeLinejoin="round"
            markerEnd={el.color ? `url(#${colorMarkerId(markerPrefix, el.color)})` : `url(#${markerPrefix}-arrow-${el.routeType})`}
          />
        );
      })}

      {/* Freehand strokes */}
      {elements.map((el) =>
        el.kind === "draw" ? (
          <polyline
            key={el.id}
            points={el.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="none"
            stroke={el.color}
            strokeWidth={el.id === selectedId ? el.width + 1.5 : el.width}
            strokeOpacity={el.highlight ? 0.35 : 1}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null,
      )}

      {/* Lines & arrows */}
      {elements.map((el) =>
        el.kind === "line" ? (
          <line
            key={el.id}
            x1={el.x1}
            y1={el.y1}
            x2={el.x2}
            y2={el.y2}
            stroke={el.color}
            strokeWidth={el.id === selectedId ? el.width + 1.5 : el.width}
            strokeDasharray={el.dash ? "8 6" : undefined}
            strokeLinecap="round"
            markerEnd={el.arrow ? `url(#${colorMarkerId(markerPrefix, el.color)})` : undefined}
          />
        ) : null,
      )}

      {/* Measurements & scale reference */}
      {elements.map((el) => {
        if (el.kind !== "measure" && el.kind !== "calibration") return null;
        const isCal = el.kind === "calibration";
        const stroke = isCal ? "#0e7a5c" : "#0f172a";
        const midX = (el.x1 + el.x2) / 2;
        const midY = (el.y1 + el.y2) / 2;
        const len = Math.hypot(el.x2 - el.x1, el.y2 - el.y1);
        const text = isCal ? `${el.metres} m (scale ref)` : (formatMetres(len, mpu) ?? "set scale to measure");
        return (
          <g key={el.id}>
            <line
              x1={el.x1}
              y1={el.y1}
              x2={el.x2}
              y2={el.y2}
              stroke={stroke}
              strokeWidth={el.id === selectedId ? 2.6 : 1.8}
              strokeDasharray={isCal ? "2 4" : "6 4"}
            />
            {[{ x: el.x1, y: el.y1 }, { x: el.x2, y: el.y2 }].map((pt, i) => (
              <line
                key={i}
                x1={pt.x - 4}
                y1={pt.y - 4}
                x2={pt.x + 4}
                y2={pt.y + 4}
                stroke={stroke}
                strokeWidth="1.8"
                transform={`rotate(${(Math.atan2(el.y2 - el.y1, el.x2 - el.x1) * 180) / Math.PI + 90} ${pt.x} ${pt.y})`}
              />
            ))}
            <g>
              <rect x={midX - text.length * 3.1 - 4} y={midY - 18} width={text.length * 6.2 + 8} height={14} rx={3} fill="rgba(255,255,255,0.88)" />
              <text x={midX} y={midY - 7.5} fontSize="10.5" fontWeight="700" fill={stroke} textAnchor="middle">
                {text}
              </text>
            </g>
          </g>
        );
      })}

      {/* Icons */}
      {elements.map((el) =>
        el.kind === "icon" ? (
          <IconBadge key={el.id} icon={el.icon} x={el.x} y={el.y} size={el.size ?? 1} color={el.color} selected={el.id === selectedId} />
        ) : null,
      )}

      {/* Labels */}
      {elements.map((el) =>
        el.kind === "label" ? (
          <text
            key={el.id}
            x={el.x}
            y={el.y}
            fontSize={el.fontSize ?? 14}
            fontWeight="700"
            fill={el.id === selectedId ? "#0e7a5c" : (el.color ?? "#0f172a")}
          >
            {el.text}
          </text>
        ) : null,
      )}

      {/* On-canvas legend */}
      {elements.map((el) =>
        el.kind === "legend" ? <CanvasLegend key={el.id} el={el} elements={elements} selected={el.id === selectedId} /> : null,
      )}

      <ScaleBar elements={elements} />

      {/* Draft previews */}
      {draftRect && (
        <g>
          {draftRect.zoneType && (
            <rect
              x={Math.min(draftRect.x, draftRect.x + draftRect.w)}
              y={Math.min(draftRect.y, draftRect.y + draftRect.h)}
              width={Math.abs(draftRect.w)}
              height={Math.abs(draftRect.h)}
              fill="none"
              stroke={ZONE_STYLE[draftRect.zoneType].stroke}
              strokeWidth="2"
              strokeDasharray="4 4"
            />
          )}
          {draftRect.shape && (
            <ShapeBody
              shape={draftRect.shape}
              x={Math.min(draftRect.x, draftRect.x + draftRect.w)}
              y={Math.min(draftRect.y, draftRect.y + draftRect.h)}
              w={Math.abs(draftRect.w)}
              h={Math.abs(draftRect.h)}
              fill="none"
              stroke={draftRect.color ?? "#0f172a"}
              strokeWidth="2"
              strokeDasharray="4 4"
            />
          )}
        </g>
      )}
      {draftRoute && draftRoute.length > 0 && draftRouteType && (
        <polyline
          points={draftRoute.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke={ROUTE_STYLE[draftRouteType].stroke}
          strokeWidth="3"
          strokeDasharray="4 4"
          strokeLinecap="round"
        />
      )}
      {draftBarrier && draftBarrier.length > 0 && draftBarrierType && (
        <polyline
          points={draftBarrier.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke={BARRIER_STYLE[draftBarrierType].stroke}
          strokeWidth="3"
          strokeDasharray="4 4"
          strokeLinecap="round"
        />
      )}
      {draftLine && (
        <line
          x1={draftLine.x1}
          y1={draftLine.y1}
          x2={draftLine.x2}
          y2={draftLine.y2}
          stroke={draftLine.color}
          strokeWidth="2"
          strokeDasharray={draftLine.dash ? "8 6" : "4 4"}
          strokeLinecap="round"
        />
      )}
      {draftDraw && draftDraw.points.length > 1 && (
        <polyline
          points={draftDraw.points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke={draftDraw.color}
          strokeWidth={draftDraw.width}
          strokeOpacity={draftDraw.highlight ? 0.35 : 1}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {children}
    </>
  );
}

/** Legend of element types actually used — rendered under both editor and print. */
export function PlanLegend({ elements }: { elements: PlanElement[] }) {
  const entries: { key: string; swatch: React.ReactNode; label: string }[] = [];
  (Object.keys(ZONE_STYLE) as ZoneType[]).forEach((zt) => {
    if (elements.some((e) => e.kind === "zone" && e.zoneType === zt)) {
      entries.push({
        key: `z-${zt}`,
        swatch: (
          <span
            className="inline-block h-3 w-5 rounded-sm border-2"
            style={{ borderColor: ZONE_STYLE[zt].stroke, background: ZONE_STYLE[zt].fill }}
          />
        ),
        label: ZONE_STYLE[zt].label,
      });
    }
  });
  (Object.keys(ROUTE_STYLE) as RouteType[]).forEach((rt) => {
    if (elements.some((e) => e.kind === "route" && e.routeType === rt)) {
      entries.push({
        key: `r-${rt}`,
        swatch: (
          <span
            className="inline-block h-0.5 w-6"
            style={{
              background: ROUTE_STYLE[rt].dash
                ? `repeating-linear-gradient(90deg, ${ROUTE_STYLE[rt].stroke} 0 5px, transparent 5px 9px)`
                : ROUTE_STYLE[rt].stroke,
            }}
          />
        ),
        label: ROUTE_STYLE[rt].label,
      });
    }
  });
  (Object.keys(BARRIER_STYLE) as BarrierType[]).forEach((bt) => {
    const s = BARRIER_STYLE[bt];
    if (elements.some((e) => e.kind === "barrier" && e.barrierType === bt)) {
      entries.push({
        key: `b-${bt}`,
        swatch: (
          <span
            className="inline-block w-6"
            style={{
              height: s.deco === "double" ? 5 : 2.5,
              background: s.dash
                ? `repeating-linear-gradient(90deg, ${s.stroke} 0 5px, transparent 5px 9px)`
                : s.deco === "double"
                  ? `linear-gradient(${s.stroke} 0 25%, white 25% 75%, ${s.stroke} 75%)`
                  : s.stroke,
            }}
          />
        ),
        label: s.label,
      });
    }
  });
  (Object.keys(ICON_META) as IconType[]).forEach((ic) => {
    if (elements.some((e) => e.kind === "icon" && e.icon === ic)) {
      entries.push({
        key: `i-${ic}`,
        swatch: <IconSwatch icon={ic} className="h-4 w-4 text-[0.55rem]" />,
        label: ICON_META[ic].label,
      });
    }
  });
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
      {entries.map((e) => (
        <span key={e.key} className="inline-flex items-center gap-1.5 text-[0.76rem] text-body">
          {e.swatch}
          {e.label}
        </span>
      ))}
    </div>
  );
}

/* ── Print output ────────────────────────────────────────────────────────── */

export function SitePlanPrint({
  value,
  background,
  heading = "Site plan sketch",
}: {
  value: PlanElement[];
  background?: PlanBackground | null;
  heading?: string;
}) {
  const markerPrefix = useId().replace(/[:]/g, "");
  if (value.length === 0 && !background?.dataUrl) return null;
  const calibrated = findCalibration(value) !== null;
  return (
    <div className="mt-5 avoid-break">
      <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">{heading}</h3>
      <div className="mt-2 overflow-hidden rounded-lg border border-line">
        <svg viewBox={`0 0 ${W} ${H}`} className="block w-full">
          <PlanSvg elements={value} background={background} markerPrefix={`p${markerPrefix}`} />
        </svg>
      </div>
      <PlanLegend elements={value} />
      <p className="mt-1.5 text-[0.72rem] text-muted">
        {calibrated
          ? "Indicative scale only — set from a user-drawn reference. Verify all distances on site before relying on this plan."
          : "Diagram only — not to scale. Verify locations on site before relying on this plan."}
      </p>
    </div>
  );
}
